import React, { useEffect, useState } from "react";
import AdminLayout from "../../components/AdminLayout";
import api, { formatApiError } from "../../lib/api";
import { Plus, Pencil, Trash2, X, UserCog } from "lucide-react";
import { toast } from "sonner";

const MAX_OPERATORS = 6;
const USERNAME_RE = /^[A-Za-z0-9]{8}$/;
const EMPTY = { name: "", username: "", password: "" };

export default function OperatorsPage() {
    const [operators, setOperators] = useState([]);
    const [buses, setBuses] = useState([]);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        const [o, b] = await Promise.all([api.get("/operators"), api.get("/buses")]);
        setOperators(o.data);
        setBuses(b.data);
    };
    useEffect(() => {
        load();
    }, []);

    const activeCount = operators.filter((o) => o.active_status !== false).length;
    const atCap = activeCount >= MAX_OPERATORS;

    const busFor = (opId) => buses.find((b) => b.operator_id === opId);

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY);
        setFormOpen(true);
    };
    const openEdit = (o) => {
        setEditing(o);
        setForm({ name: o.name, username: o.username || "", password: "" });
        setFormOpen(true);
    };

    const save = async (e) => {
        e.preventDefault();
        const username = form.username.trim().toUpperCase();
        if (!USERNAME_RE.test(username)) {
            toast.error("Username must be exactly 8 alphanumeric characters");
            return;
        }
        if (!editing && form.password.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }
        setSaving(true);
        try {
            if (editing) {
                const payload = { name: form.name, username };
                if (form.password) payload.password = form.password;
                await api.patch(`/operators/${editing.id}`, payload);
                toast.success("Operator updated");
            } else {
                await api.post("/operators", { name: form.name, username, password: form.password });
                toast.success("Operator added");
            }
            setFormOpen(false);
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (o) => {
        try {
            await api.patch(`/operators/${o.id}`, { active_status: !(o.active_status !== false) });
            toast.success(o.active_status !== false ? "Operator deactivated" : "Operator activated");
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
        }
    };

    const remove = async (o) => {
        if (!window.confirm(`Delete operator ${o.name}?`)) return;
        await api.delete(`/operators/${o.id}`);
        toast.success("Operator deleted");
        load();
    };

    return (
        <AdminLayout>
            <div className="p-4 sm:p-8 lg:p-12 max-w-[1400px]">
                <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                    <div>
                        <p className="label-caps mb-2">Management</p>
                        <h1 className="font-display text-5xl text-[#111111]">Operators</h1>
                        <p className="text-[#666666] mt-2">
                            {activeCount} / {MAX_OPERATORS} active — configured by Admin only
                        </p>
                    </div>
                    <button
                        onClick={openCreate}
                        disabled={atCap}
                        data-testid="add-operator-button"
                        title={atCap ? `Maximum of ${MAX_OPERATORS} operators reached` : undefined}
                        className="bg-[#FF3B30] text-white font-bold px-6 py-3 hover:bg-[#D70015] flex items-center gap-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#FF3B30]"
                    >
                        <Plus size={18} /> Add Operator
                    </button>
                </div>

                {atCap && (
                    <div className="bg-[#F8F9FA] border border-[#E5E5E5] px-4 py-3 text-sm text-[#666666] mb-6">
                        Maximum of {MAX_OPERATORS} active operators reached. Deactivate or delete one to add another.
                    </div>
                )}

                <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F8F9FA] border-b-2 border-[#111111]">
                            <tr>
                                <th className="text-left font-bold py-3 px-4">Name</th>
                                <th className="text-left font-bold py-3 px-4">Username</th>
                                <th className="text-left font-bold py-3 px-4">Assigned Bus</th>
                                <th className="text-left font-bold py-3 px-4">Status</th>
                                <th className="text-right font-bold py-3 px-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody data-testid="operators-table">
                            {operators.map((o) => (
                                <tr key={o.id} className="border-b border-[#E5E5E5] hover:bg-[#F8F9FA]">
                                    <td className="py-3 px-4 font-medium">{o.name}</td>
                                    <td className="py-3 px-4 font-mono text-xs">{o.username}</td>
                                    <td className="py-3 px-4 text-[#666666]">
                                        {busFor(o.id)?.bus_name || "— Unassigned —"}
                                    </td>
                                    <td className="py-3 px-4">
                                        <button
                                            onClick={() => toggleActive(o)}
                                            data-testid={`toggle-active-${o.username}`}
                                            className={`font-bold px-2 py-1 text-xs uppercase tracking-wider border transition-colors ${
                                                o.active_status !== false
                                                    ? "bg-[#34C759]/10 text-[#34C759] border-[#34C759]/30 hover:bg-[#34C759]/20"
                                                    : "bg-[#666666]/10 text-[#666666] border-[#666666]/30 hover:bg-[#666666]/20"
                                            }`}
                                        >
                                            {o.active_status !== false ? "Active" : "Inactive"}
                                        </button>
                                    </td>
                                    <td className="py-3 px-4">
                                        <div className="flex gap-1 justify-end">
                                            <button
                                                onClick={() => openEdit(o)}
                                                data-testid={`edit-operator-${o.username}`}
                                                title="Edit"
                                                className="p-2 hover:bg-[#111111] hover:text-white transition-colors"
                                            >
                                                <Pencil size={16} />
                                            </button>
                                            <button
                                                onClick={() => remove(o)}
                                                data-testid={`delete-operator-${o.username}`}
                                                title="Delete"
                                                className="p-2 hover:bg-[#FF3B30] hover:text-white transition-colors"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {operators.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="p-8 text-center text-[#666666]">
                                        <UserCog className="mx-auto mb-2" size={24} />
                                        No operators yet. Add up to {MAX_OPERATORS}.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {formOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white border-2 border-[#111111] w-full max-w-md animate-slide-up">
                        <div className="flex justify-between items-center px-6 py-4 border-b-2 border-[#111111]">
                            <h3 className="font-display text-2xl">{editing ? "Edit" : "Add"} Operator</h3>
                            <button onClick={() => setFormOpen(false)} data-testid="close-operator-form" className="p-1 hover:bg-[#F8F9FA]">
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={save} className="p-6 space-y-4">
                            <div>
                                <label className="label-caps block mb-2">Full Name</label>
                                <input
                                    required
                                    data-testid="operator-name-input"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="w-full bg-[#F8F9FA] border border-[#E5E5E5] px-4 py-2 focus:border-[#111111] outline-none"
                                    placeholder="Ravi Kumar"
                                />
                            </div>
                            <div>
                                <label className="label-caps block mb-2">Username (8 alphanumeric chars)</label>
                                <input
                                    required
                                    data-testid="operator-username-input"
                                    value={form.username}
                                    maxLength={8}
                                    onChange={(e) => setForm({ ...form, username: e.target.value.toUpperCase() })}
                                    className="w-full bg-[#F8F9FA] border border-[#E5E5E5] px-4 py-2 focus:border-[#111111] outline-none font-mono uppercase"
                                    placeholder="OPRTR001"
                                />
                            </div>
                            <div>
                                <label className="label-caps block mb-2">
                                    Password {editing && <span className="normal-case font-normal text-[#666666]">(leave blank to keep current)</span>}
                                </label>
                                <input
                                    type="password"
                                    data-testid="operator-password-input"
                                    required={!editing}
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    className="w-full bg-[#F8F9FA] border border-[#E5E5E5] px-4 py-2 focus:border-[#111111] outline-none"
                                    placeholder="••••••••"
                                />
                            </div>
                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setFormOpen(false)}
                                    className="flex-1 border border-[#111111] font-bold py-3 hover:bg-[#F8F9FA]"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    data-testid="operator-form-submit"
                                    className="flex-1 bg-[#FF3B30] text-white font-bold py-3 hover:bg-[#D70015] disabled:opacity-60"
                                >
                                    {editing ? "Save Changes" : "Add Operator"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
}
